/**
 * CurrencyCard — reusable currency-picker card.
 *
 * Rendered as one of the cards inside the CRM defaults section
 * (`CrmDefaultsSection`); no longer a standalone settings section with its own
 * nav entry. It still resolves its own org context and permissions and owns the
 * currency save flow. The edit gate checks `ahoy_permissions_admin`
 * (TEAMS_ADMIN_PERMISSION) to mirror the server-side enforcement in
 * TeamSerializer.validate_default_currency, so the control is only enabled for
 * users whose PATCH will actually be accepted (avoids leading a non-admin to a
 * 400 on Save).
 */

import type { DefaultCurrencyEnum, Team } from 'api-client';
import { OrgsApi } from 'api-client';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { getApiConfiguration } from '@/api/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { logger } from '@/lib/logger';

import { useOrg } from '../../../../contexts/OrgContext';
import { TEAMS_ADMIN_PERMISSION } from '../settings-permissions';
import { CurrencyPicker } from './CurrencyPicker';
import { SettingsCard } from './SettingsCard';
import { useMountedOrgGuard } from './use-mounted-org-guard';

function DefaultCurrencyForm({ canEdit }: { canEdit: boolean }) {
  const { currentOrg, updateOrgFields } = useOrg();

  const savedCurrency: DefaultCurrencyEnum =
    currentOrg?.defaultCurrency || 'USD';
  const [selectedCurrency, setSelectedCurrency] =
    useState<DefaultCurrencyEnum>(savedCurrency);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isMountedRef, currentOrgIdRef } = useMountedOrgGuard(currentOrg?.id);

  const hasChanges = selectedCurrency !== savedCurrency;

  // Reset the form whenever the org changes (id), not only when the saved
  // value changes — otherwise an unsaved selection can leak into the next org.
  useEffect(() => {
    setSelectedCurrency(savedCurrency);
  }, [currentOrg?.id, savedCurrency]);

  const handleCurrencyChange = useCallback((code: string) => {
    setSelectedCurrency(code as DefaultCurrencyEnum);
    setError(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!currentOrg?.id) {
      setError('Organization not found');
      return;
    }

    const requestOrgId = currentOrg.id;
    const patch: Partial<Team> = { defaultCurrency: selectedCurrency };
    setIsSaving(true);
    setError(null);

    try {
      const api = new OrgsApi(getApiConfiguration());
      await api.teamsPartialUpdate(
        { id: requestOrgId, patchedTeam: patch },
        { signal: AbortSignal.timeout(30000) }
      );

      // Atomically patch just this field on both currentOrg and the cached
      // availableOrgs entry via a functional merge — keeps
      // switch-org-and-back from showing a stale value. Guarded by id inside
      // OrgContext, so a save that lands after switching orgs still updates
      // the correct cached record without touching the active org.
      updateOrgFields(requestOrgId, patch);

      if (isMountedRef.current && currentOrgIdRef.current === requestOrgId) {
        toast.success('Currency setting saved');
      }
    } catch (err: unknown) {
      logger.error(
        'Failed to update default currency',
        err instanceof Error ? err : new Error(String(err)),
        { org_id: requestOrgId }
      );
      // Gate error feedback by the request's org id (not just mount state) so a
      // failed save for a since-abandoned org doesn't surface an error against
      // the newly selected org.
      if (isMountedRef.current && currentOrgIdRef.current === requestOrgId) {
        setError("We couldn't save the currency setting. Try again.");
        toast.error("We couldn't update the currency. Try again.");
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [
    currentOrg?.id,
    selectedCurrency,
    updateOrgFields,
    isMountedRef,
    currentOrgIdRef,
  ]);

  return (
    <div>
      <div className='space-y-4'>
        <CurrencyPicker
          label='Default currency'
          hint='Existing deals are not affected.'
          value={selectedCurrency}
          onChange={handleCurrencyChange}
          isDisabled={isSaving || !canEdit}
          size='sm'
        />

        {!canEdit && (
          <p className='text-sm text-fg-tertiary'>
            Only workspace admins can change the default currency.
          </p>
        )}

        {error && <p className='text-sm text-error-primary'>{error}</p>}
      </div>

      {canEdit && (
        <div className='mt-4 flex justify-end'>
          <Button
            color='primary'
            size='md'
            onClick={handleSave}
            isDisabled={!hasChanges || isSaving}
            isLoading={isSaving}
          >
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
}

export function CurrencyCard() {
  const { hasPermissionGroup } = usePermissions();
  const canEdit = hasPermissionGroup(TEAMS_ADMIN_PERMISSION);

  return (
    <SettingsCard>
      <h3 className='text-lg font-semibold text-fg-primary'>Currency</h3>
      <p className='text-sm text-fg-tertiary mt-0.5'>
        Set the default currency for your workspace.
      </p>
      <div className='mt-4'>
        <DefaultCurrencyForm canEdit={canEdit} />
      </div>
    </SettingsCard>
  );
}
